import { notification } from 'antd';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import router, { useRouter } from 'next/router';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, UseFormHandleSubmit, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { unstable_serialize, useSWRConfig } from 'swr';
import { InternalConfiguration } from 'swr/_internal';
import { EditTicketFormValues } from '../../../types/form';
import { EditOrganizationFormValues } from '../../../types/organizationform';
import { EditUserFormValues } from '../../../types/userform';
import useCurrentTicket from '../../hooks/ticketDetailPage/useCurrentTicket';
import useFormName from '../../hooks/ticketDetailPage/useFormName';
import useTicketFieldDefinitionList, {
    getUIFriendlyTicketFieldDefinitionList
} from '../../hooks/ticketDetailPage/useTicketFieldDefinitionList';
import useTicketKey from '../../hooks/useTicketKey';
import { convertTicketFieldsDataToFormValues } from '../../layouts/Pages/TicketPagesContentLayout/TicketDetailContentLayout';
import {
    EditTicketPostData,
    EditTicketReturnedData,
    FieldDefinitionMap,
    FieldRuleOnForm,
    TICKET_FORM_FIELDS_KEY,
    TicketSystemFieldsKey,
    TicketSystemFieldsKeyMap,
    actionAfterSubmit,
    channelTypeMap
} from '../../model/types';
import { editTicket } from '../../services/tickets/tickets';
import { useMacroStore, useTabStore, useTicketFormUtilStore } from '../../store/RootStoreContext';
import { getSafeTicketKeyFromUrl, getSelectedTabKeyFromUrl } from '../../store/childStore/tabStore';
import {
    BasicFormMethodsContextInterface,
    BasicFormMethodsProvider
} from '../../store/provider/BasicFormMethodsProvider';
import {
    controlledUserFormValueToPostTicketData,
    parseSerializedValueFromUserFormValue,
    parseUserIdFromSerializedValue
} from '../../utils/forms/formHelper.parser';
import { removeFormRelatedStorageItems } from '../../utils/forms/formHelpers';
import { nonNullishValue } from '../../utils/helpers';
import requestWithMessage from '../../utils/requestWithMessage';
import { pushTo } from '../../utils/routeHelper';
import {
    StorageHelperKeys,
    getLocalStorageItem,
    getSessionStorageItem,
    removeSessionStorageItem
} from '../../utils/storageHelpers';
import { filterSWRKeys } from '../../utils/swr';
import { fireResetRichTextEditorStateEvent } from '../GrispiRichTextEditor/plugins/ResetEditorStatePlugin';
import { TicketLifecycleEventName, fireTicketLifecycleEvent } from '../TicketActivityListener';
import { isChannelGSocial } from '../TicketPage/CommentTypeSelect';
import { getMentionedUsersFromComment } from '../TicketPage/MentionedUserController';
import { hasFieldUserValueType } from './CreateTicketForm';
import FormPersistController from './FormPersistController';

interface Props {
    defaultValues: EditTicketFormValues;
}

export function removeUnchangedTicketFieldValues<
    T extends EditTicketFormValues | EditOrganizationFormValues | EditUserFormValues
>(formValues: T, defaultValues: T): T {
    const { fields } = formValues;
    const { fields: defaultFieldValues } = defaultValues;

    const changedTicketFieldValues = {};
    Object.entries(fields).forEach(([fieldKey, fieldValue]) => {
        if (fieldValue !== defaultFieldValues[fieldKey]) {
            changedTicketFieldValues[fieldKey] = fieldValue;
        }
    });
    formValues['fields'] = changedTicketFieldValues;
    return formValues;
}

const getEditTicketPostDataFromFormValues = ({
    defaultValues,
    formValues,
    fieldListMap,
    cacheOption: { cache, session }
}: {
    defaultValues: EditTicketFormValues;
    formValues: EditTicketFormValues;
    fieldListMap: FieldDefinitionMap;
    cacheOption: { cache: InternalConfiguration['cache']; session: Session };
}): EditTicketPostData => {
    const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        channel,
        comment = {} as EditTicketPostData['comment'],
        currentAttachments: { inlineImages, attachments },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        formName,
        fields,
        ...rest
    } = formValues;

    const currentActiveFieldListCacheKey = unstable_serialize([
        `/forms/${fields[TicketSystemFieldsKey.formId.replace(new RegExp('\\.', 'gi'), '$')]}`,
        session.userInfo.username,
        session.tenantId
    ]);
    const currentActiveFieldList = (cache.get(currentActiveFieldListCacheKey)?.data?.fields as FieldRuleOnForm[])?.map(
        (field) => field.key
    );

    const postDataFields: EditTicketPostData['fields'] = [];
    Object.entries(removeUnchangedTicketFieldValues(formValues, defaultValues)['fields']).forEach(([key, value]) => {
        const postKey = key.replaceAll('$', '.');

        if (currentActiveFieldList.includes(postKey)) {
            const postValue =
                typeof value === 'string'
                    ? hasFieldUserValueType(fieldListMap[postKey])
                        ? controlledUserFormValueToPostTicketData(value)
                        : value
                    : value;

            postDataFields.push({
                key: postKey,
                value: postValue
            });
        }
    });

    const { body } = comment;
    const filteredInlineImages = inlineImages.filter(({ src }) => body.includes(src)).map(({ id }) => id);
    const attachmentIds = [...filteredInlineImages, ...attachments.map((v) => v.id)];

    const mentionedUsers = getMentionedUsersFromComment(comment.body)
        .map(parseSerializedValueFromUserFormValue)
        .map(parseUserIdFromSerializedValue);

    return {
        id: getSafeTicketKeyFromUrl(router.asPath),
        ...rest,
        ...(comment?.body
            ? {
                  comment: {
                      ...comment,
                      channel: comment.channel ?? channelTypeMap.WEB,
                      ...(isChannelGSocial(comment?.channel) ? { externalId: comment?.externalId } : {}),
                      mentionedUsers,
                      // optional prop for attachments if any
                      ...(attachmentIds.length > 0 && {
                          attachmentIds
                      })
                  }
              }
            : { channel: channelTypeMap.WEB }),
        fields: postDataFields
    };
};

function TicketEditingActivityController() {
    const ticketKey = useTicketKey();
    const { isDirty } = useFormState();
    useEffect(() => {
        isDirty && fireTicketLifecycleEvent(TicketLifecycleEventName.TicketEdited, ticketKey);
    }, [isDirty, ticketKey]);

    return null;
}

const EditTicketForm = ({ children, defaultValues }: PropsWithChildren<Props>) => {
    const { t: tTickets } = useTranslation('tickets');
    const { t: tError } = useTranslation('error');
    const { cache, mutate } = useSWRConfig();
    const { data: session } = useSession();
    const {
        asPath,
        query: { ticketKey }
    } = useRouter();
    const formName = useFormName();
    const { data: ticketDetail, mutate: mutateTicket } = useCurrentTicket();
    const { data: allFields } = useTicketFieldDefinitionList(null, { revalidateOnMount: true });

    const [formHydrated, setFormHydrated] = useState(getSessionStorageItem(formName) ? false : true);

    const { resetFileList } = useTicketFormUtilStore();
    const { closeTabAndGetNewTargetUrl, updateTicketTabs } = useTabStore();
    const { initPlaceholderValues } = useMacroStore();

    const [api, notificationOnSubmitError] = notification.useNotification();

    const methods = useForm<EditTicketFormValues>({
        mode: 'onChange',
        defaultValues,
        shouldFocusError: true
        // reValidateMode: 'onSubmit'
    });

    const { getValues, handleSubmit, reset, setValue, trigger } = methods;
    useEffect(() => {
        const persistedFormValues = getSessionStorageItem(formName);

        if (persistedFormValues && !formHydrated) {
            Object.entries(persistedFormValues).forEach(([key, value]) => {
                if (nonNullishValue(value) && typeof value === 'object' && !Array.isArray(value)) {
                    Object.entries(value).forEach(([subKey, subValue]) => {
                        if (nonNullishValue(subValue) && typeof subValue === 'object' && !Array.isArray(subValue)) {
                            Object.entries(subValue).forEach(([subSubKey, subSubValue]) => {
                                setValue(`${key}.${subKey}.${subSubKey}` as any, subSubValue, { shouldDirty: true });
                            });
                            return;
                        }

                        setValue(`${key}.${subKey}` as any, subValue, { shouldDirty: true });
                    });
                }
                setValue(key as any, value, { shouldDirty: true });
            });
            setFormHydrated(true);
            trigger();
            return;
        }
    }, [formHydrated, formName, setValue, trigger]);

    const onValid: SubmitHandler<EditTicketFormValues> = useCallback(
        async (formValues) => {
            const postData = getEditTicketPostDataFromFormValues({
                defaultValues,
                formValues,
                fieldListMap: getUIFriendlyTicketFieldDefinitionList(allFields).listMap,
                cacheOption: { cache, session }
            });

            const startMessage = tTickets('message.onUpdateStart');
            const finishMessage = tTickets('message.onUpdateFinish');
            await requestWithMessage({
                key: 'create-ticket',
                startMessage,
                finishMessage,
                apiRequest: async () => {
                    const actionAfterSubmit: actionAfterSubmit = getLocalStorageItem(
                        StorageHelperKeys.ACTION_AFTER_SUBMIT
                    );

                    const returnedData: EditTicketReturnedData = await editTicket(postData);

                    resetFileList(true);

                    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);
                    if (actionAfterSubmit === 'stay') {
                        removeFormRelatedStorageItems(formValues.formName);

                        await mutateTicket();
                        await mutate(filterSWRKeys(`/changes/${ticketKey}`));

                        updateTicketTabs(returnedData);
                        const resetTicketValues = {
                            ...formValues,
                            comment: { ...defaultValues.comment, creator: formValues.comment.creator },
                            currentAttachments: { ...defaultValues.currentAttachments },
                            [TICKET_FORM_FIELDS_KEY]: convertTicketFieldsDataToFormValues(returnedData)
                        };
                        //if any fields were removed and not exists in resetTicketValues, set them to null.
                        //defaultValues can't be undefined https://react-hook-form.com/docs/usecontroller/controller
                        Object.keys(defaultValues.fields).forEach((key) => {
                            if (resetTicketValues[TICKET_FORM_FIELDS_KEY][key] === undefined) {
                                resetTicketValues[TICKET_FORM_FIELDS_KEY][key] = null;
                            }
                        });
                        reset(resetTicketValues);

                        fireResetRichTextEditorStateEvent({ eventListenerKey: formValues.formName });
                        initPlaceholderValues(returnedData);
                        await mutate(filterSWRKeys('/tickets/mine-recent'));
                        return;
                    }

                    const targetUrl = closeTabAndGetNewTargetUrl(selectedTabKey);
                    await mutate(filterSWRKeys('/tickets/mine-recent'));
                    targetUrl && pushTo(targetUrl);
                }
            });
        },
        [
            defaultValues,
            allFields,
            cache,
            session,
            tTickets,
            resetFileList,
            asPath,
            closeTabAndGetNewTargetUrl,
            mutate,
            mutateTicket,
            ticketKey,
            updateTicketTabs,
            reset,
            initPlaceholderValues
        ]
    );

    const onSubmit = useCallback(
        (handleOnSubmit: ReturnType<UseFormHandleSubmit<EditTicketFormValues>>) => (e?: React.BaseSyntheticEvent) =>
            handleOnSubmit(e).catch((error) => {
                if (error.name === 'AxiosError') {
                    // to convert isSubmitting value to false without any data lost
                    reset(getValues(), {
                        keepDirtyValues: true,
                        keepErrors: true,
                        keepDirty: true,
                        keepValues: true,
                        keepDefaultValues: true,
                        keepIsSubmitted: true,
                        keepTouched: true,
                        keepIsValid: true,
                        keepSubmitCount: true
                    });
                    return;
                }
                api.info({
                    message: tError('ticketSubmissionError.title'),
                    description: tError('ticketSubmissionError.message'),
                    duration: 0
                });
            }),
        [api, getValues, reset, tError]
    );

    // we're able to reach below methods via context easily
    const basicFormMethods: BasicFormMethodsContextInterface = useMemo(
        () => ({
            submit: () => {
                trigger().then((isValid) => {
                    !isValid &&
                        setValue(
                            `${TICKET_FORM_FIELDS_KEY}.${TicketSystemFieldsKeyMap.status}`,
                            defaultValues[TICKET_FORM_FIELDS_KEY][TicketSystemFieldsKeyMap.status],
                            { shouldValidate: true }
                        );
                    isValid && onSubmit(handleSubmit(onValid))();
                });
            },
            resetForm: (formName: string) => {
                removeSessionStorageItem(formName);
                resetFileList(true);
                fireResetRichTextEditorStateEvent({ eventListenerKey: formName });

                reset(defaultValues);
                // should re-init to be able to pick the new values
                initPlaceholderValues(ticketDetail);
            }
        }),
        [
            handleSubmit,
            initPlaceholderValues,
            onSubmit,
            onValid,
            reset,
            resetFileList,
            setValue,
            trigger,
            ticketDetail,
            defaultValues
        ]
    );

    return (
        <BasicFormMethodsProvider value={basicFormMethods}>
            <FormProvider {...methods}>
                <FormPersistController formName={formName} />
                <TicketEditingActivityController />
                {notificationOnSubmitError}
                <form className="full-height" onSubmit={onSubmit(handleSubmit(onValid))}>
                    {children}
                </form>
            </FormProvider>
        </BasicFormMethodsProvider>
    );
};
export default EditTicketForm;
